import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Edit2, Save } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

export default function LeavePolicyPage() {
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: r }, { data: t }] = await Promise.all([
        api.get("/leave/policy", { params: { include_inactive: true } }),
        api.get("/roles"),
        api.get("/lookups", { params: { category: "leave_type" } }),
      ]);
      setItems(p.items || []);
      setRoles((r.items || []).filter((x) => !["SUPER_ADMIN", "DISTRIBUTOR"].includes(x.code)));
      setTypes(t.items || []);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (p) => {
    if (!window.confirm(`Delete policy ${p.role_code} / ${p.leave_type_code}?`)) return;
    try {
      await api.delete(`/leave/policy/${p.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="admin-leave-policy-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Masters" }, { label: "Leave policy" }]}
        title="Leave policy"
        subtitle="Annual leave entitlements and approval chain configuration."
        favorite={{ key: "admin.leave-policy", type: "screen", label: "Leave policy", route: "/admin/leave-policy" }}
        testid="leavepolicypage-page-header"
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Leave policy</h1>
          <p className="text-sm text-neutral-500">Annual leave entitlements per role and leave type. Overrides can be applied per user.</p>
        </div>
        <Button onClick={() => setEditing({ role_code: "", leave_type_code: "", annual_balance_days: 12 })}
                className="tulsi-gradient text-white" data-testid="policy-new">
          <Plus className="h-4 w-4 mr-2" /> Add policy
        </Button>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid="policy-table">
            <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-500 tracking-widest">
              <tr>
                <th className="text-left py-3 px-4">Role</th>
                <th className="text-left py-3 px-4">Leave Type</th>
                <th className="text-right py-3 px-4">Annual Days</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={5} className="py-10 text-center text-neutral-400">Loading…</td></tr>}
              {!loading && items.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-neutral-400">No policies</td></tr>}
              {items.map((p) => (
                <tr key={p.id} className="border-t border-neutral-100" data-testid={`policy-row-${p.role_code}-${p.leave_type_code}`}>
                  <td className="py-3 px-4 font-semibold">{p.role_code}</td>
                  <td className="py-3 px-4">{types.find((t) => t.code === p.leave_type_code)?.label || p.leave_type_code}</td>
                  <td className="py-3 px-4 text-right font-mono">{p.annual_balance_days}</td>
                  <td className="py-3 px-4">
                    <span className={`text-[11px] px-2 py-0.5 rounded-full ${p.is_active ? "bg-green-100 text-green-800" : "bg-neutral-100 text-neutral-500"}`}>
                      {p.is_active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <Button variant="ghost" size="sm" onClick={() => setEditing(p)} data-testid={`policy-edit-${p.role_code}-${p.leave_type_code}`}>
                      <Edit2 className="h-3.5 w-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => del(p)} className="text-red-600">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {editing && (
        <PolicyForm p={editing} roles={roles} types={types}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function PolicyForm({ p, roles, types, onClose, onSaved }) {
  const [form, setForm] = useState(p);
  const [busy, setBusy] = useState(false);
  const isNew = !p.id;

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/leave/policy", form);
        toast.success("Policy created");
      } else {
        await api.patch(`/leave/policy/${form.id}`, {
          annual_balance_days: Number(form.annual_balance_days),
          is_active: form.is_active,
        });
        toast.success("Policy updated");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="policy-form-dialog">
        <DialogHeader><DialogTitle>{isNew ? "New leave policy" : "Edit policy"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Role</Label>
            <Select value={form.role_code} disabled={!isNew}
                    onValueChange={(v) => setForm({ ...form, role_code: v })}>
              <SelectTrigger data-testid="policy-role"><SelectValue placeholder="Choose role" /></SelectTrigger>
              <SelectContent>
                {roles.map((r) => <SelectItem key={r.code} value={r.code}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Leave Type</Label>
            <Select value={form.leave_type_code} disabled={!isNew}
                    onValueChange={(v) => setForm({ ...form, leave_type_code: v })}>
              <SelectTrigger data-testid="policy-type"><SelectValue placeholder="Choose type" /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t.code} value={t.code}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Annual Balance (days)</Label>
            <Input type="number" min={0} max={365} value={form.annual_balance_days}
                   onChange={(e) => setForm({ ...form, annual_balance_days: Number(e.target.value) })}
                   data-testid="policy-days" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="tulsi-gradient text-white" data-testid="policy-save">
            <Save className="h-4 w-4 mr-2" /> Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
